import { useMemo } from 'react';
import MiniRadar from './MiniRadar.jsx';
import {
    ARCHETYPE_SUPERVISOR_HINT,
    MANADA_ACT_HINT,
    MANADA_ENERGY_HINT,
    MANADA_RHYTHM_HINT,
    RADAR_ARCHETYPE_AXES,
} from '../utils/constants.js';
import { buildManadaInsights, quitLabel, quitEmoji } from '../utils/supervisorManada.js';
import { ENNEAGRAM_TYPES, HD_TYPES } from '../utils/enneagramHd.js';

/** Comprensión de manada: radar, arquetipos y riesgo de huida. */
export default function SupervisorManadaPanel({ teamData, onSelectWorker }) {
    const insights = useMemo(() => buildManadaInsights(teamData), [teamData]);

    if (!teamData?.length) {
        return (
            <p className="sup-team-hint">
                Aún no hay datos de manada. Cuando tu equipo registre jornada y encuestas semanales, verás gráficos
                aquí.
            </p>
        );
    }

    return (
        <div className="sup-manada-panel">
            <p className="sup-manada-lead">
                Composición de tu manada ({insights.total} personas). Datos orientativos para conversar, no
                evaluación.
            </p>

            <div className="sup-manada-radar-block">
                <p className="sup-panel-section-label">Arquetipos ALINEA</p>
                <p className="sup-manada-mini-hint">{MANADA_ENERGY_HINT}</p>
                <MiniRadar data={insights.radarData} axes={RADAR_ARCHETYPE_AXES} size={210} />
                <div className="sup-manada-bars">
                    {insights.archetypeBars.map((bar) => {
                        const axis = RADAR_ARCHETYPE_AXES.find((a) => a.key === bar.key);
                        return (
                        <div key={bar.key} className="sup-manada-bar-block">
                            <div className="sup-manada-bar-row" title={`${bar.name}: ${bar.count}`}>
                                <span className="sup-manada-bar-emoji" aria-hidden>
                                    {bar.emoji}
                                </span>
                                <span
                                    className="sup-manada-bar-label"
                                    style={{ color: axis?.color || '#475569' }}
                                >
                                    {axis?.label || bar.name.split(' ').slice(-1)[0]}
                                </span>
                                <span className="sup-manada-bar-track">
                                    <span
                                        className="sup-manada-bar-fill"
                                        style={{
                                            width: `${Math.max(bar.pct, 4)}%`,
                                            background: axis?.color || '#0f766e',
                                        }}
                                    />
                                </span>
                                <span className="sup-manada-bar-count">{bar.count}</span>
                            </div>
                            <p className="sup-manada-bar-desc">{ARCHETYPE_SUPERVISOR_HINT[bar.key]}</p>
                        </div>
                        );
                    })}
                </div>
            </div>

            {Object.keys(insights.enneagramDist).length > 0 && (
                <div className="sup-manada-block">
                    <p className="sup-panel-section-label">Ritmo natural del equipo</p>
                    <p className="sup-manada-mini-hint">{MANADA_RHYTHM_HINT}</p>
                    <div className="sup-manada-mini-bars">
                        {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((type) => {
                            const count = insights.enneagramDist[type] || 0;
                            if (!count) return null;
                            const info = ENNEAGRAM_TYPES[type];
                            const pct = Math.round((count / insights.total) * 100);
                            return (
                                <div key={type} className="sup-manada-mini-row" title={info.name}>
                                    <span>{info.emoji}</span>
                                    <span className="sup-manada-mini-track">
                                        <span className="sup-manada-mini-fill" style={{ width: `${pct}%` }} />
                                    </span>
                                    <span className="sup-manada-mini-n">{count}</span>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {Object.keys(insights.hdDist).length > 0 && (
                <div className="sup-manada-block">
                    <p className="sup-panel-section-label">Formas de actuar</p>
                    <p className="sup-manada-mini-hint">{MANADA_ACT_HINT}</p>
                    <div className="sup-manada-hd-chips">
                        {Object.entries(insights.hdDist).map(([key, count]) => (
                            <span key={key} className="sup-manada-hd-chip">
                                {HD_TYPES[key]?.emoji} {HD_TYPES[key]?.name?.split(' ')[0]}: {count}
                            </span>
                        ))}
                    </div>
                </div>
            )}

            <div className="sup-manada-huida">
                <p className="sup-panel-section-label">Riesgo de huida (encuesta semanal)</p>
                <p className="sup-manada-huida-summary">
                    <strong>{insights.huidaAlto.length}</strong> con pensamiento fuerte de irse (
                    {insights.huidaPctAlto}% del equipo)
                    {insights.huidaMedio.length > 0 && (
                        <>
                            {' '}
                            · <strong>{insights.huidaMedio.length}</strong> a veces
                        </>
                    )}
                    {insights.sinEncuestaSemanal > 0 && (
                        <> · {insights.sinEncuestaSemanal} sin encuesta reciente</>
                    )}
                </p>
                {insights.huidaAlto.length === 0 && insights.huidaMedio.length === 0 ? (
                    <p className="sup-team-hint">Por ahora nadie reporta riesgo alto de salida.</p>
                ) : (
                    <ul className="sup-manada-huida-list">
                        {[...insights.huidaAlto, ...insights.huidaMedio].map((row) => {
                            const isAlto = insights.huidaAlto.some((r) => r.id === row.id);
                            const quitKey = isAlto ? 'si_mucho' : 'a_veces';
                            return (
                                <li key={row.id}>
                                    <button
                                        type="button"
                                        className={`sup-manada-huida-btn${isAlto ? ' high' : ''}`}
                                        onClick={() => onSelectWorker?.(row.id)}
                                    >
                                        <span aria-hidden>{quitEmoji(quitKey)}</span>
                                        {row.nombre?.split(' ')[0]}
                                        <span className="sup-manada-huida-tag">{quitLabel(quitKey)}</span>
                                    </button>
                                </li>
                            );
                        })}
                    </ul>
                )}
            </div>
        </div>
    );
}
